import { GetMisspelledWordDocument } from "@/generated/graphql";
import type { Passage, SpellCheckStatus } from "@ai-teach/types";
import { useApolloClient } from "@apollo/client/react";

export function useSpellChecker() {
  const client = useApolloClient();

  const getMisspelledWord = async (options: {
    variables: { text: string; whitelist?: string[] };
  }) => {
    const result = await client.query({
      query: GetMisspelledWordDocument,
      variables: options.variables,
      fetchPolicy: "network-only",
    });
    return result;
  };

  const checkAllPassages = async (passages: Passage[]) => {
    // Check spelling for all passages
    const spellCheckPromises = passages.map(async (passage) => {
      // An empty passage has zero misspelled words — don't let that read as "passed".
      if (!passage.text?.trim()) {
        return { passageId: passage.id, status: "unchecked" as SpellCheckStatus, words: [] };
      }

      const response = await getMisspelledWord({
        variables: {
          text: passage.text || "",
          whitelist: passage.whitelist || [],
        },
      });
      const misspelledWords = response.data?.getMisspelledWord || [];

      return {
        passageId: passage.id,
        status: (misspelledWords.length === 0 ? "passed" : "failed") as SpellCheckStatus,
        words: misspelledWords.map((word) => word.word),
      };
    });

    const results = await Promise.all(spellCheckPromises);

    // Update all passages with spell check results
    const updatedPassages = passages.map((passage) => {
      const result = results.find((r) => r?.passageId === passage.id);
      return {
        ...passage,
        spellCheckStatus: result?.status || "unchecked",
      };
    });

    // Calculate summary stats
    const passedCount = results.filter((r) => r.status === "passed").length;
    const failedCount = results.filter((r) => r.status === "failed").length;

    const misspelledByPassage = Object.fromEntries(
      results.filter((r) => r.passageId).map((r) => [r.passageId as string, r.words])
    );

    return {
      updatedPassages,
      passedCount,
      failedCount,
      misspelledByPassage,
    };
  };

  return { getMisspelledWord, checkAllPassages };
}
